// Itinerary. A real plan, stored server side, day by day, with create, read,
// update and delete against /trips/:tripId/itinerary.
//
// Two things share each day and neither replaces the other: the PLAN, what
// the traveller intends, and the DIARY ENTRIES, what actually happened,
// ordered by `capturedAt` so a trip written up a month later still lands on
// the right day. A day with nothing on it is drawn as an empty day rather
// than skipped, because the gap in a plan is information.
//
// Connected, not standalone: the trip picker writes the app's active trip,
// so choosing here changes Beranda, the map, Budget and Stats too. A
// destination sent over from the wishlist arrives in `itineraryDraft` and
// prefills the add form.
//
// The round 7 routes may not exist on a given API build. On 404 the screen
// says planning is unavailable and still renders the days it can derive
// from the trip itself, the same way the geocoder degrades.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, IconPen, IconPin, IconPlan, IconPlus, IconTrash } from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { apiClient, apiIssueMessage } from "../lib/api";
import { dayKey, dayKeyOf, formatDayHeading, formatDateShort, formatNumber } from "../lib/format";
import { navigate } from "../lib/router";
import type { ItineraryItem } from "../lib/types";
import { useApp } from "../state/app";

const DAY_MS = 24 * 60 * 60 * 1000;
/** Gap between stops, so a future drag always has a midpoint to land on. */
const POSITION_STEP = 100;
const MAX_DAYS = 60;

type LoadState = "loading" | "ready" | "unavailable" | "failed";

interface DraftForm {
  /** The day this form belongs to, and the itinerary item it edits. */
  date: string;
  id: string | null;
  placeId: string | null;
  placeName: string;
  lat: number | null;
  lng: number | null;
  note: string;
}

export function ItineraryScreen() {
  const {
    t,
    locale,
    trips,
    activeTrip,
    activeTripId,
    setActiveTripId,
    moments,
    refreshMoments,
    wishlist,
    itineraryDraft,
    setItineraryDraft,
  } = useApp();

  const [items, setItems] = useState<ItineraryItem[]>([]);
  const [load, setLoad] = useState<LoadState>("loading");
  const [form, setForm] = useState<DraftForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyFrom, setCopyFrom] = useState("");
  const [copied, setCopied] = useState<number | null>(null);

  useEffect(() => {
    if (activeTripId) void refreshMoments(activeTripId);
  }, [activeTripId, refreshMoments]);

  const reload = useCallback(async (tripId: string) => {
    setLoad("loading");
    try {
      const fresh = await apiClient.listItinerary(tripId);
      if (fresh === null) {
        setItems([]);
        setLoad("unavailable");
        return;
      }
      setItems(fresh);
      setLoad("ready");
    } catch {
      setItems([]);
      setLoad("failed");
    }
  }, []);

  useEffect(() => {
    if (!activeTripId) {
      setLoad("ready");
      return;
    }
    void reload(activeTripId);
  }, [activeTripId, reload]);

  const days = useMemo(() => {
    if (!activeTrip) return [];
    const start = new Date(`${activeTrip.startDate}T00:00:00`);
    const endSource = activeTrip.endDate
      ? new Date(`${activeTrip.endDate}T00:00:00`)
      : new Date(Math.max(start.getTime(), Date.now()));
    const span = Math.min(
      MAX_DAYS,
      Math.max(1, Math.round((endSource.getTime() - start.getTime()) / DAY_MS) + 1),
    );
    const buckets = new Map<string, typeof moments>();
    for (const moment of moments) {
      const key = dayKey(moment.capturedAt);
      const list = buckets.get(key) ?? [];
      list.push(moment);
      buckets.set(key, list);
    }

    // The trip's own days, plus any day that actually carries something. A
    // copied plan can land outside the trip range, and a stop that exists but
    // is never drawn is lost data, not a tidy screen.
    const keys = new Set<string>();
    for (let index = 0; index < span; index += 1) {
      keys.add(dayKeyOf(new Date(start.getTime() + index * DAY_MS)));
    }
    for (const item of items) keys.add(item.date.slice(0, 10));

    return [...keys]
      .sort((a, b) => a.localeCompare(b))
      .map((key) => {
        const [y = "0", m = "1", d = "1"] = key.split("-");
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        const index = Math.round((date.getTime() - start.getTime()) / DAY_MS);
        const entries = (buckets.get(key) ?? [])
          .slice()
          .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
        const plan = items
          .filter((item) => item.date.slice(0, 10) === key)
          .sort((a, b) => a.position - b.position);
        return { index, key, date, entries, plan, inRange: index >= 0 && index < span };
      });
  }, [activeTrip, moments, items]);

  /**
   * A destination handed over from the wishlist opens the add form on the
   * first day of the trip, prefilled. Read once and cleared, so going back
   * to Itinerary later does not reopen a form nobody asked for.
   */
  useEffect(() => {
    if (!itineraryDraft || !activeTrip) return;
    setForm({
      date: activeTrip.startDate.slice(0, 10),
      id: null,
      placeId: itineraryDraft.placeId,
      placeName: itineraryDraft.placeName,
      lat: itineraryDraft.lat,
      lng: itineraryDraft.lng,
      note: "",
    });
    setItineraryDraft(null);
  }, [itineraryDraft, activeTrip, setItineraryDraft]);

  async function submit(): Promise<void> {
    if (!activeTripId || !form || !form.placeName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (form.id) {
        const updated = await apiClient.updateItineraryItem(form.id, {
          placeId: form.placeId,
          placeName: form.placeName.trim(),
          lat: form.lat,
          lng: form.lng,
          note: form.note.trim() || null,
          date: form.date,
        });
        setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        // `position` is a SORT KEY, not an index. A new stop lands one step
        // past the last one on its day, and nothing else is renumbered.
        const onThatDay = items.filter((item) => item.date.slice(0, 10) === form.date);
        const position =
          onThatDay.length === 0
            ? POSITION_STEP
            : Math.max(...onThatDay.map((item) => item.position)) + POSITION_STEP;
        const created = await apiClient.createItineraryItem(activeTripId, {
          date: form.date,
          placeId: form.placeId,
          placeName: form.placeName.trim(),
          lat: form.lat,
          lng: form.lng,
          note: form.note.trim() || null,
          position,
        });
        setItems((current) => [...current, created]);
        setLoad("ready");
      }
      setForm(null);
    } catch (failure) {
      // "must fall inside the trip range, 2026-09-01 to 2026-09-05" is worth
      // far more to a traveller than "the stop could not be saved", so the
      // server's own message wins whenever it gave one.
      setError(apiIssueMessage(failure) ?? t("itinerary.saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await apiClient.deleteItineraryItem(id);
      setItems((current) => current.filter((item) => item.id !== id));
    } catch (failure) {
      setError(apiIssueMessage(failure) ?? t("itinerary.deleteFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function copyPlan(): Promise<void> {
    if (!activeTripId || !copyFrom) return;
    setBusy(true);
    setError(null);
    setCopied(null);
    try {
      const result = await apiClient.copyItinerary(activeTripId, copyFrom);
      setCopied(result.copied ?? 0);
      await reload(activeTripId);
    } catch (failure) {
      // A refused copy names both spans ("that plan spans 2 days and this
      // trip has room for 1"). It refuses rather than clamping, so the
      // traveller has to be told which trip to lengthen.
      setError(apiIssueMessage(failure) ?? t("itinerary.copyFailed"));
    } finally {
      setBusy(false);
    }
  }

  if (!activeTrip) {
    return (
      <div className="screen-pad">
        <EmptyState
          glyph={<IconPlan />}
          title={t("itinerary.empty")}
          hint={t("itinerary.emptyHint")}
          action={
            <button type="button" className="btn btn-quiet" onClick={() => navigate("trips")}>
              {t("trips.title")}
            </button>
          }
        />
      </div>
    );
  }

  const otherTrips = trips.filter((trip) => trip.id !== activeTripId);
  const canPlan = load === "ready";

  return (
    <div className="screen-pad">
      <div className="setting-list">
        <label className="setting-row">
          <span className="setting-label">{t("itinerary.trip")}</span>
          <select
            className="select"
            value={activeTripId ?? ""}
            onChange={(event) => setActiveTripId(event.target.value)}
          >
            {trips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {trip.title}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="section-sub">
        {t("itinerary.lede")} {formatDateShort(activeTrip.startDate, locale)}
        {activeTrip.endDate
          ? ` ${t("trips.until")} ${formatDateShort(activeTrip.endDate, locale)}`
          : ""}
      </p>

      {load === "unavailable" ? <p className="field-note">{t("itinerary.unavailable")}</p> : null}
      {load === "failed" ? <p className="field-note">{t("itinerary.loadFailed")}</p> : null}
      {load === "loading" ? <p className="field-note">{t("common.loading")}</p> : null}
      {error ? <p className="field-note">{error}</p> : null}

      <div className="plan-list">
        {days.map((day) => (
          <article className="plan-day" key={day.key}>
            <div className="plan-day-head">
              <h3 className="plan-day-title">
                {day.inRange
                  ? `${t("itinerary.day")} ${formatNumber(day.index + 1, locale)}`
                  : t("itinerary.outsideRange")}
              </h3>
              <span className="plan-day-date">{formatDayHeading(day.key, locale)}</span>
            </div>

            {canPlan ? (
              day.plan.length === 0 ? (
                <p className="plan-day-empty">{t("itinerary.dayPlanEmpty")}</p>
              ) : (
                <ul className="plan-entries">
                  {day.plan.map((item) => (
                    <li className="plan-stop" key={item.id}>
                      <Icon size={16} color="var(--color-text-muted)">
                        <IconPin />
                      </Icon>
                      <span className="plan-stop-text">
                        <span className="plan-entry-place">{item.placeName}</span>
                        {item.note ? (
                          <span className="plan-entry-caption">{item.note}</span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={t("itinerary.editStop")}
                        disabled={busy}
                        onClick={() =>
                          setForm({
                            date: day.key,
                            id: item.id,
                            placeId: item.placeId,
                            placeName: item.placeName,
                            lat: item.lat,
                            lng: item.lng,
                            note: item.note ?? "",
                          })
                        }
                      >
                        <Icon size={18}>
                          <IconPen />
                        </Icon>
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={t("itinerary.deleteStop")}
                        disabled={busy}
                        onClick={() => void remove(item.id)}
                      >
                        <Icon size={18}>
                          <IconTrash />
                        </Icon>
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : null}

            {day.entries.length > 0 ? (
              <ul className="plan-entries">
                {day.entries.map((entry) => (
                  <li className="plan-entry" key={entry.id}>
                    <Icon size={16} color="var(--color-text-muted)">
                      <IconPin />
                    </Icon>
                    <span className="plan-entry-place">
                      {entry.placeName || t("explore.noPlace")}
                    </span>
                    <span className="plan-entry-caption">
                      {entry.caption || t("diary.noCaption")}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            {!canPlan && day.entries.length === 0 ? (
              <p className="plan-day-empty">{t("itinerary.dayEmpty")}</p>
            ) : null}

            {canPlan && form?.date === day.key ? (
              <div className="plan-form">
                <label className="field">
                  <span className="field-label">{t("itinerary.place")}</span>
                  <input
                    className="input"
                    value={form.placeName}
                    maxLength={120}
                    placeholder={t("itinerary.placePlaceholder")}
                    onChange={(event) =>
                      setForm({ ...form, placeName: event.target.value, placeId: null })
                    }
                  />
                </label>

                {wishlist.length > 0 ? (
                  <label className="field">
                    <span className="field-label">{t("itinerary.fromWishlist")}</span>
                    <select
                      className="select select-block"
                      value={form.placeId ?? ""}
                      onChange={(event) => {
                        const saved = wishlist.find(
                          (item) => item.placeId === event.target.value,
                        );
                        setForm(
                          saved
                            ? {
                                ...form,
                                placeId: saved.placeId,
                                placeName: saved.name,
                                lat: saved.lat,
                                lng: saved.lng,
                              }
                            : { ...form, placeId: null },
                        );
                      }}
                    >
                      <option value="">{t("itinerary.wishlistNone")}</option>
                      {wishlist.map((item) => (
                        <option key={item.placeId} value={item.placeId}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <label className="field">
                  <span className="field-label">{t("itinerary.note")}</span>
                  <input
                    className="input"
                    value={form.note}
                    maxLength={200}
                    placeholder={t("itinerary.notePlaceholder")}
                    onChange={(event) => setForm({ ...form, note: event.target.value })}
                  />
                </label>

                <div className="row-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !form.placeName.trim()}
                    onClick={() => void submit()}
                  >
                    {t("itinerary.save")}
                  </button>
                  <button type="button" className="btn btn-quiet" onClick={() => setForm(null)}>
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : null}

            {canPlan && form?.date !== day.key ? (
              <button
                type="button"
                className="btn btn-quiet btn-block plan-add"
                onClick={() =>
                  setForm({
                    date: day.key,
                    id: null,
                    placeId: null,
                    placeName: "",
                    lat: null,
                    lng: null,
                    note: "",
                  })
                }
              >
                <Icon size={18}>
                  <IconPlus />
                </Icon>
                {t("itinerary.addHere")}
              </button>
            ) : null}
          </article>
        ))}
      </div>

      {canPlan ? (
        <section className="card">
          <h2 className="card-title">{t("itinerary.copyTitle")}</h2>
          <p className="field-note">{t("itinerary.copyHint")}</p>
          {otherTrips.length === 0 ? (
            <p className="field-note">{t("itinerary.noOtherTrip")}</p>
          ) : (
            <>
              <label className="field">
                <span className="field-label">{t("itinerary.copyFrom")}</span>
                <select
                  className="select select-block"
                  value={copyFrom}
                  onChange={(event) => setCopyFrom(event.target.value)}
                >
                  <option value="">{t("itinerary.chooseTrip")}</option>
                  {otherTrips.map((trip) => (
                    <option key={trip.id} value={trip.id}>
                      {trip.title}
                    </option>
                  ))}
                </select>
              </label>
              <div className="row-actions">
                <button
                  type="button"
                  className="btn btn-quiet"
                  disabled={!copyFrom || busy}
                  onClick={() => void copyPlan()}
                >
                  {t("itinerary.copy")}
                </button>
                {copied !== null ? (
                  <span className="field-note" role="status">
                    {formatNumber(copied, locale)} {t("itinerary.copied")}
                  </span>
                ) : null}
              </div>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}
